import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { EuiFlexGroup, EuiFlexItem, EuiInMemoryTable } from "@elastic/eui";
import { cleanObj, getTableStyles, toTitleLabel } from "utils";
import FDFormControl from "components/Forms/FDFormControl";
import TablePanel from "components/Panels/TablePanel";
import { IControl } from "types/app";
import { defaultOperatorList, evaluationFrequencyList } from "Consts";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import CustomQueryService from "services/AdminConsole/Monitoring/CustomQuery/CustomQueryService";
import Button from "components/Buttons/Button";

const CustomAlerts = ({ isEditMode, customSource, setCustomSource, setSelectedTabId }) => {
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [queryOptions, setQueryOptions] = useState<any>([]);
  const [columnOptions, setColumnOptions] = useState<any>([]);
  const { clientId } = useSelector(globalFilterSelector);
  const [exampleColumns, setExampleColumns] = useState<any>([]);
  const [exampleData, setExampleData] = useState<any>([]);

  const configureTolerance = [
    customSource?.id,
    customSource?.valueColumnName,
    customSource?.valueName,
    customSource?.operator,
  ]?.every((i) => i?.length > 0);

  useEffect(() => {
    CustomQueryService.lookupCustomQueries({ clientId }).then((result) => {
      const options = result?.customQueries?.map((i) => ({
        label: i.name,
        value: i.id,
        text: i.name,
        ...i,
      }));

      setQueryOptions(options);
    });
  }, [clientId, customSource]);

  useEffect(() => {
    if (customSource?.id?.length > 0) {
      CustomQueryService.getCustomQueryResult({
        clientId,
        customQueryId: customSource?.id,
      }).then((result) => {
        const options = result.columnNames?.map((i) => ({
          label: i,
          value: i,
          text: i,
        }));

        if (result?.columnNames?.length === 1) {
          setCustomSource({ ...customSource, valueColumnName: result?.columnNames?.[0] });
          setCustomSource({ ...customSource, valueName: toTitleLabel(result?.columnNames?.[0]) });
        }

        setColumnOptions(options);

        setExampleColumns(
          result.columnNames?.map((i) => ({
            name: i,
            field: i,
          }))
        );

        setExampleData(
          result.data?.map((item) => {
            const obj = {};
            item.forEach((i, index) => {
              obj[result?.columnNames[index]] = i;
            });
            return obj;
          })
        );
      });
    } else {
      setColumnOptions([]);
    }
  }, [customSource?.id]);

  const formControls: Record<string, Partial<IControl>> = {
    id: {
      field: "id",
      inputType: "select",
      label: "Custom Query",
      placeholder: "Select a query...",
      options: queryOptions,
      required: isEditMode,
      readonly: !isEditMode,
    },
    valueColumnName: {
      field: "valueColumnName",
      inputType: "select",
      label: "Value Column",
      options: columnOptions,
      placeholder: "Select column...",
      required: isEditMode,
      readonly: !isEditMode,
      helpText: "The column that will be evaluated for the alert.",
    },
    valueName: {
      field: "valueName",
      inputType: "text",
      label: "Value Name",
      placeholder: "Name of the value column...",
      required: isEditMode,
      readonly: !isEditMode,
      helpText: "Description of the column.",
    },
    objectColumnName: {
      field: "objectColumnName",
      inputType: "select",
      label: "Object Column (Optional)",
      options: columnOptions?.filter((i) => i.label !== customSource?.valueColumnName),
      placeholder: "Select object type...",
      readonly: !isEditMode,
      helpText: "Column used to identify distinct objects.",
    },
    objectName: {
      field: "objectName",
      inputType: "text",
      label: "Object Name",
      placeholder: "Name of the object...",
      readonly: !isEditMode,
      helpText: "Description of the object column.",
      required: customSource?.objectColumnName?.length > 0,
    },
    operator: {
      field: "operator",
      inputType: "select",
      label: "Operator",
      options: defaultOperatorList,
      placeholder: "Select operator...",
      required: isEditMode,
      readonly: !isEditMode,
    },
    evaluationFrequency: {
      field: "evaluationFrequency",
      inputType: "select",
      label: "Evaluation Frequency",
      options: evaluationFrequencyList,
      placeholder: "Select evaluation frequency...",
      required: isEditMode,
      readonly: !isEditMode,
      hideSelectOption: true,
    },
  };

  const onChange = (control: IControl, value: any, errMessage: string) => {
    setCustomSource((current) => {
      const s = {
        ...current,
        [control.field]: value,
        ...(control.field === "valueColumnName" && {
          valueName: toTitleLabel(value),
        }),
        ...(control.field === "objectColumnName" && {
          objectName: toTitleLabel(value),
        }),
      };
      return s;
    });

    setValidationErrors((current) => {
      return cleanObj({
        ...current,
        [control.field]: errMessage,
        ...(control.field === "objectColumnName" && {
          objectName: "",
        }),
      });
    });
  };

  return (
    <div>
      <EuiFlexGroup
        direction="column"
        style={{
          border: "1px dashed #14A05A",
          borderRadius: "10px",
          padding: "16px",
        }}>
        <EuiFlexItem style={{ color: "#14A05A" }}>
          ✨ Configure custom query-based alerting for this alert type
        </EuiFlexItem>
        <EuiFlexItem>
          <FDFormControl
            control={formControls.id}
            errorMessage={validationErrors.id}
            onChange={onChange}
            source={customSource}
            fullWidth
          />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiFlexGroup gutterSize="l">
            <EuiFlexItem>
              <FDFormControl
                control={formControls.valueColumnName}
                errorMessage={validationErrors.valueColumnName}
                onChange={onChange}
                source={customSource}
                fullWidth
              />
            </EuiFlexItem>

            <EuiFlexItem>
              <FDFormControl
                control={formControls.valueName}
                errorMessage={validationErrors.valueName}
                onChange={onChange}
                source={customSource}
                fullWidth
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>

        {columnOptions?.length > 1 && (
          <EuiFlexItem>
            <EuiFlexGroup gutterSize="l">
              <EuiFlexItem>
                <FDFormControl
                  control={formControls.objectColumnName}
                  errorMessage={validationErrors.objectColumnName}
                  onChange={onChange}
                  source={customSource}
                  fullWidth
                />
              </EuiFlexItem>

              <EuiFlexItem>
                <FDFormControl
                  control={formControls.objectName}
                  errorMessage={validationErrors.objectName}
                  onChange={onChange}
                  source={customSource}
                  fullWidth
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        )}
        <EuiFlexItem>
          <EuiFlexGroup gutterSize="l">
            <EuiFlexItem>
              <FDFormControl
                control={formControls.operator}
                errorMessage={validationErrors.operator}
                onChange={onChange}
                source={customSource}
                fullWidth
              />
            </EuiFlexItem>

            <EuiFlexItem>
              <FDFormControl
                control={formControls.evaluationFrequency}
                errorMessage={validationErrors.evaluationFrequency}
                onChange={onChange}
                source={customSource}
                fullWidth
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem>
          {setSelectedTabId && configureTolerance && (
            <Button
              type="empty"
              text="Configure Tolerance"
              iconType={"arrowRight"}
              iconSide="right"
              style={{ marginTop: "24px" }}
              onClick={() => {
                setSelectedTabId("Tolerance");
              }}
            />
          )}
        </EuiFlexItem>
      </EuiFlexGroup>

      {customSource?.id?.length > 0 && (
        <div style={{ paddingTop: "32px" }}>
          <TablePanel
            columns={exampleColumns}
            data={exampleData}
            panelTitleProps={{
              title: `${queryOptions?.find((i) => i.id === customSource?.id)?.name} query sample results`,
            }}
            showPanel={false}>
            <EuiInMemoryTable
              className={getTableStyles([])}
              columns={exampleColumns}
              items={exampleData?.filter((_, idx) => idx <= 4)}
              tableLayout="auto"
            />
          </TablePanel>
        </div>
      )}
    </div>
  );
};

export default CustomAlerts;
